/**
 * The submissions inbox's score-range and date-range filters, given a surface.
 *
 * WHY THIS EXISTS
 * ---------------
 * `SubmissionFilters` has carried `minScore`, `maxScore` and `dateRange` since
 * it was written. They are applied by `useFilteredSubmissions` and counted by
 * `activeFilterCount` — and were rendered NOWHERE. No user could set them, so
 * they could never be non-default, so the filtering code behind them and the
 * badge that counts them were both permanently dead.
 *
 * The screen even passed `activeFilterCount` to `SearchBar` without passing
 * `onFilterPress`, and `SearchBar` only draws the filter button when it has a
 * handler — so the way in did not render either.
 *
 * WHY NOT `FilterPanel`
 * ---------------------
 * `SearchFilters.tsx` already exports a `FilterPanel`, but it is built for the
 * DEALFLOW filter shape: industry, funding stage, and the seven dealflow
 * statuses. Pointing it at submissions would offer an investor filters that do
 * not apply to an inbox. This renders only the two types submissions actually
 * has and nothing else.
 *
 * Status is deliberately absent: the inbox filters status through the chip row
 * above the list, and offering it in two places is how two controls end up
 * disagreeing.
 */

import { useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';

import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import type { SubmissionFilters } from '@/lib/state/submissions-store';

/** Score presets, because typing two numbers to say "good ones" is a chore. */
const SCORE_PRESETS: { label: string; min: number | null; max: number | null }[] = [
  { label: 'Any', min: null, max: null },
  { label: '60+', min: 60, max: null },
  { label: '70+', min: 70, max: null },
  { label: '80+', min: 80, max: null },
];

const DATE_RANGES: { value: SubmissionFilters['dateRange']; label: string }[] = [
  { value: 'all', label: 'Any time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

interface Props {
  visible: boolean;
  onClose: () => void;
  filters: SubmissionFilters;
  onUpdateFilter: <K extends keyof SubmissionFilters>(key: K, value: SubmissionFilters[K]) => void;
  onReset: () => void;
}

function Chip({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  const colors = useThemeColors();
  return (
    <View
      className="rounded-full overflow-hidden shrink-0"
      style={{
        backgroundColor: selected ? colors.primary[50] : colors.surface.secondary,
        borderWidth: 1,
        borderColor: selected ? colors.primary[300] : colors.border.light,
      }}
    >
      <Pressable className="px-3.5 py-2" onPress={onPress} accessibilityLabel={label}>
        <Text
          numberOfLines={1}
          className="text-sm font-medium"
          style={{ color: selected ? colors.primary[700] : colors.text.secondary }}
        >
          {label}
        </Text>
      </Pressable>
    </View>
  );
}

export function SubmissionFilterSheet({
  visible,
  onClose,
  filters,
  onUpdateFilter,
  onReset,
}: Props) {
  const colors = useThemeColors();

  // Local text state so a half-typed number ("7" on the way to "70") is not
  // committed as a filter on every keystroke.
  const [minText, setMinText] = useState(filters.minScore?.toString() ?? '');
  const [maxText, setMaxText] = useState(filters.maxScore?.toString() ?? '');

  const commitScore = (raw: string, key: 'minScore' | 'maxScore') => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      onUpdateFilter(key, null);
      return;
    }
    const n = Number.parseInt(trimmed, 10);
    // An unparseable or out-of-range entry clears rather than clamps: silently
    // rewriting what someone typed is how a filter starts lying about itself.
    onUpdateFilter(key, Number.isNaN(n) || n < 0 || n > 100 ? null : n);
  };

  const activePreset = SCORE_PRESETS.find(
    (p) => p.min === filters.minScore && p.max === filters.maxScore,
  );

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="Filter submissions"
      footer={
        <AppModal.Actions>
          <ModalAction
            label="Reset"
            variant="neutral"
            onPress={() => {
              setMinText('');
              setMaxText('');
              onReset();
            }}
          />
          <ModalAction label="Done" variant="primary" onPress={onClose} />
        </AppModal.Actions>
      }
    >
      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-2">
        Score
      </Text>
      <View className="flex-row flex-wrap gap-2 mb-3">
        {SCORE_PRESETS.map((p) => (
          <Chip
            key={p.label}
            label={p.label}
            selected={activePreset?.label === p.label}
            onPress={() => {
              setMinText(p.min?.toString() ?? '');
              setMaxText(p.max?.toString() ?? '');
              onUpdateFilter('minScore', p.min);
              onUpdateFilter('maxScore', p.max);
            }}
          />
        ))}
      </View>

      <View className="flex-row gap-3 mb-5">
        <View className="flex-1 px-3 py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
            Min
          </Text>
          <TextInput
            value={minText}
            onChangeText={setMinText}
            onBlur={() => commitScore(minText, 'minScore')}
            onSubmitEditing={() => commitScore(minText, 'minScore')}
            keyboardType="number-pad"
            placeholder="0"
            placeholderTextColor={colors.text.tertiary}
            style={{ color: colors.text.primary }}
            className="text-base"
            accessibilityLabel="Minimum score"
          />
        </View>
        <View className="flex-1 px-3 py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
            Max
          </Text>
          <TextInput
            value={maxText}
            onChangeText={setMaxText}
            onBlur={() => commitScore(maxText, 'maxScore')}
            onSubmitEditing={() => commitScore(maxText, 'maxScore')}
            keyboardType="number-pad"
            placeholder="100"
            placeholderTextColor={colors.text.tertiary}
            style={{ color: colors.text.primary }}
            className="text-base"
            accessibilityLabel="Maximum score"
          />
        </View>
      </View>

      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-2">
        Received
      </Text>
      <View className="flex-row flex-wrap gap-2">
        {DATE_RANGES.map((r) => (
          <Chip
            key={r.value}
            label={r.label}
            selected={filters.dateRange === r.value}
            onPress={() => onUpdateFilter('dateRange', r.value)}
          />
        ))}
      </View>
    </AppModal>
  );
}
